import { YStack, XStack, Text, isWeb, useMedia } from 'tamagui'
import { CoverImage } from '@components/CoverImage'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome'
import { faCalendarDays } from '@fortawesome/pro-light-svg-icons/faCalendarDays'
import { faComment } from '@fortawesome/pro-light-svg-icons/faComment'
import { IProductPage } from '@dtos/ProductPage'
import { ASIDE_STICKY_STYLE, CARD_BRAND_ICONS } from './constants'
import { ctaKind } from './helpers'
import { usePricing, useResolvedColors } from './hooks'
import { BuyButton } from './BuyButton'
import { SecurityNote } from './SecurityNote'
import { AnchorPrice, CurrentPrice, InstallmentLine } from './PriceParts'

function useAvailabilityLabel(product: IProductPage) {
  const { t } = useTranslation('ProductPage')

  if (product.availableTimeType === 'time' && product.availableTimeLength) {
    return t('availability.time', { days: product.availableTimeLength })
  }
  if (product.availableTimeType === 'date' && product.expireDate) {
    return t('availability.date', {
      date: new Date(product.expireDate).toLocaleDateString('pt-BR'),
    })
  }
  return t('availability.indeterminate')
}

function AsideDetails({ product }: { product: IProductPage }) {
  const { t } = useTranslation('ProductPage')
  const colors = useResolvedColors()
  const availability = useAvailabilityLabel(product)

  return (
    <YStack gap={16}>
      <XStack gap={8} alignItems="center">
        <FontAwesomeIcon
          icon={faCalendarDays}
          size={16}
          color={colors.accent}
        />
        <Text color="$text-primary" fontSize={14} fontFamily="$mulishRegular">
          {availability}
        </Text>
      </XStack>
      <XStack gap={8} alignItems="center">
        <FontAwesomeIcon icon={faComment} size={16} color={colors.accent} />
        <Text color="$text-primary" fontSize={14} fontFamily="$mulishRegular">
          {t('digital')}
        </Text>
      </XStack>
      {product.course?.kind === 'live_meet' && (
        <Text color="$text-primary" fontSize={14} fontFamily="$mulishRegular">
          {t('availability.live-meet-quantity', {
            quantity: product.course.contentsCount,
          })}
        </Text>
      )}
    </YStack>
  )
}

function CardBrands({ brands }: { brands: string[] }) {
  const colors = useResolvedColors()
  const icons = brands
    .map((brand) => ({ brand, icon: CARD_BRAND_ICONS[brand] }))
    .filter((item) => item.icon)

  if (!icons.length) return null

  return (
    <XStack gap={6} alignItems="center">
      {icons.map(({ brand, icon }) => (
        <FontAwesomeIcon
          key={brand}
          icon={icon}
          size={26}
          color={colors.cardBrands}
        />
      ))}
    </XStack>
  )
}

// Aside (productPage_aside): imagem, preço, CTA, segurança, bandeiras e
// detalhes com ícones na cor de destaque. Acompanha o scroll no desktop.
export function Aside({ product }: { product: IProductPage }) {
  const { t } = useTranslation('ProductPage')
  const media = useMedia()
  const {
    option,
    isFree,
    period,
    price,
    anchorPrice,
    installment,
    installmentKey,
  } = usePricing(product)
  const thumbnail = product.videoCover?.thumbnail || product.logo

  return (
    <YStack
      width={media.gtSm ? 345 : '100%'}
      flexShrink={0}
      gap={24}
      style={isWeb && media.gtSm ? ASIDE_STICKY_STYLE : undefined}
      testID="product-aside"
    >
      {thumbnail ? (
        <CoverImage
          uri={thumbnail}
          alt={product.title ?? ''}
          width="100%"
          height={194}
          borderRadius={4}
        />
      ) : null}

      {option && !isFree && (
        <YStack gap={4}>
          <XStack alignItems="baseline" gap={8} flexWrap="wrap">
            {anchorPrice && <AnchorPrice value={anchorPrice} fontSize={14} />}
            <CurrentPrice
              price={price}
              period={period}
              color="$text-primary"
              fontSize={14}
              periodFontSize={14}
            />
          </XStack>
          {installment && (
            <InstallmentLine
              installment={installment}
              installmentKey={installmentKey}
              color="$text-primary"
              fontSize={14}
            />
          )}
        </YStack>
      )}

      <YStack gap={12} width="100%" alignItems="flex-start">
        <BuyButton
          label={t(
            `ctaSecondary.${ctaKind(option, product.course?.kind ?? null)}`,
          )}
          url={option?.checkoutUrl ?? null}
          location="aside"
          product={product}
          fullWidth
        />
        {option?.checkoutUrl && !isFree ? <SecurityNote /> : null}
      </YStack>

      {!isFree && <CardBrands brands={product.supportedCardBrands} />}

      <AsideDetails product={product} />
    </YStack>
  )
}
